import { Container } from "@mui/material";
import { Suspense, useEffect, useState } from "react";
import HeaderBreadcrumbs from "src/components/HeaderBreadcrumbs";
import NoAccessNavigate from "src/components/NoAccessNavigate";
import Page from "src/components/Page";
import useLocales from "src/hooks/useLocales";
import usePermissions from "src/hooks/usePermissions";
import lazyWithRetry from "src/lazyWithRetry";
import SkeletonStepper from "src/pages/ManualDocumentV2/components/skeleton/stepper";
import { PATH_DASHBOARD } from "src/routes/paths";
import type { IFormConfig } from "../core/types";
import {
  ACCOUNT_CLASS_ROUTE,
  getDocumentClassRoute,
  getDocumentListingHref,
} from "./utils/breadcrumbRoutes";

const DocumentForm = lazyWithRetry(() => import("./DocumentForm"));

interface IDocumentPageProps {
  config: IFormConfig;
}

const DocumentPage = ({ config }: IDocumentPageProps) => {
  const { translate } = useLocales();
  const { permissionHandle } = usePermissions();
  const [refreshPage, setRefreshPage] = useState(0);

  useEffect(() => {
    return () => {
      delete (window as any).pdfjsLib;
      delete (window as any).pdfjsWorker;
      delete (window as any).pdfjsTestingUtils;
    };
  }, []);

  if (!permissionHandle([config.permission])) return <NoAccessNavigate />;

  const accountClassRoute = ACCOUNT_CLASS_ROUTE[config.accountClass];
  const documentClassRoute = getDocumentClassRoute(
    config.documentClass,
    config.accountClass,
  );
  const listingHref = getDocumentListingHref(
    config.documentClass,
    config.accountClass,
  );

  return (
    <Page title={translate(config.id)}>
      <Container maxWidth={false}>
        <HeaderBreadcrumbs
          links={[
            { name: translate("Dashboard"), href: PATH_DASHBOARD.root },
            ...(accountClassRoute && documentClassRoute
              ? [
                  { name: translate(accountClassRoute) },
                  { name: translate(documentClassRoute), href: listingHref },
                ]
              : [
                  {
                    name: translate("documents"),
                    href: PATH_DASHBOARD.general.documents.root,
                  },
                ]),
            { name: translate(config.id) },
          ]}
        />

        <Suspense fallback={<SkeletonStepper />}>
          <DocumentForm
            config={config}
            refreshPage={refreshPage}
            onSeriesUpdated={() => setRefreshPage((p) => p + 1)}
          />
        </Suspense>
      </Container>
    </Page>
  );
};

export default DocumentPage;
